import React, { useState, useEffect } from "react";
import "./TemplateBrowser.css";
import TemplateHelp from "./TemplateHelp";

/**
 * Replace merge fields with sample data for preview
 */
const replaceMergeFields = (htmlContent) => {
  if (!htmlContent) return "";

  const sampleData = {
    "{{businessName}}": "Sample Business",
    "{{customerFirstName}}": "John",
    "{{customerLastName}}": "Doe",
    "{{customerName}}": "John Doe",
    "{{promotionTitle}}": "Special Promotion",
    "{{promotionCode}}": "SAVE20",
    "{{discountPercent}}": "20%",
    "{{productName}}": "Premium Service",
    "{{productPrice}}": "$99.99",
    "{{announcementTitle}}": "Important Announcement",
    "{{businessLogo}}": "",
    "{{productImageUrl}}": "",
    "{{heroImageUrl}}": "",
    "{{businessAddress}}": "123 Main Street, City, State 12345",
    "{{businessPhone}}": "(555) 123-4567",
    "{{businessEmail}}": "contact@business.com",
    "{{businessUrl}}": "/business/sample-business",
    "{{bookingUrl}}": "/business/sample-business/main-location",
    "{{unsubscribeLink}}": "#",
    "{{currentYear}}": new Date().getFullYear().toString(),
  };

  let previewContent = htmlContent;
  Object.entries(sampleData).forEach(([field, value]) => {
    previewContent = previewContent.replace(new RegExp(field, "g"), value);
  });

  return previewContent;
};

/**
 * TemplateBrowser Component
 * Allows users to browse and select from pre-built email templates
 * or start from scratch with a blank canvas
 */
export default function TemplateBrowser({
  isOpen,
  onClose,
  onSelect,
  businessId,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showHelp, setShowHelp] = useState(false);

  const categories = [
    { value: "all", label: "All Templates" },
    { value: "welcome", label: "Welcome Emails" },
    { value: "newsletter", label: "Newsletters" },
    { value: "promotion", label: "Promotions" },
    { value: "product", label: "Product Showcase" },
    { value: "announcement", label: "Announcements" },
    { value: "custom", label: "My Templates" },
  ];

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedCategory, businessId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("ogestyle_token");
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      if (businessId) {
        params.append("businessId", businessId);
      }

      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
      const url = `${apiBaseUrl}/api/marketing/templates?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch templates: ${response.status} ${response.statusText}`,
        );
      }

      // Check if response is actually JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server returned non-JSON response (${contentType})`);
      }

      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    onSelect(template);
    onClose();
  };

  const handleStartFromScratch = () => {
    onSelect(null); // null means blank template
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="template-browser-overlay" onClick={onClose}>
        <div
          className="template-browser-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="template-browser-header">
            <h2>Choose a Template</h2>
            <div className="header-actions">
              <button
                className="help-button"
                onClick={() => setShowHelp(true)}
                title="How to customize templates"
              >
                <span>❓</span> Help
              </button>
              <button
                className="close-button"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="template-categories">
            {categories.map((cat) => (
              <button
                key={cat.value}
                className={`category-button ${selectedCategory === cat.value ? "active" : ""}`}
                onClick={() => setSelectedCategory(cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="template-browser-content">
            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading templates...</p>
              </div>
            )}

            {error && (
              <div className="error-state">
                <p>❌ {error}</p>
                <button onClick={fetchTemplates} className="retry-button">
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                {/* Blank Template Option */}
                <div className="template-grid">
                  <div
                    className="template-card blank-template"
                    onClick={handleStartFromScratch}
                  >
                    <div className="template-thumbnail blank">
                      <div className="blank-icon">+</div>
                    </div>
                    <div className="template-info">
                      <h3>Start from Scratch</h3>
                      <p>Create your own email design</p>
                    </div>
                  </div>

                  {/* Template Cards */}
                  {templates.map((template) => (
                    <div
                      key={template._id}
                      className="template-card"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="template-thumbnail">
                        {/* Show HTML preview in iframe */}
                        <iframe
                          srcDoc={`<style>body { font-size: 12px !important; } h1, h2, h3 { font-size: 14px !important; } p, li, a, td, span { font-size: 12px !important; } footer, .footer, small { font-size: 10px !important; }</style>${replaceMergeFields(template.content)}`}
                          title={template.templateName}
                          className="template-preview-iframe"
                          sandbox=""
                        />
                        <div className="template-overlay">
                          <button className="use-template-button">
                            Use Template
                          </button>
                        </div>
                      </div>
                      <div className="template-info">
                        <h3 title={template.templateName}>
                          {template.templateName}
                        </h3>
                        <span
                          className="template-category"
                          title={template.templateCategory}
                        >
                          {template.templateCategory}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {templates.length === 0 && (
                  <div className="empty-state">
                    <p>No templates found in this category.</p>
                    {selectedCategory === "custom" && (
                      <p className="empty-hint">
                        💡 Save your campaigns as templates to see them here.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <>
          <div
            className="template-browser-overlay"
            onClick={() => setShowHelp(false)}
          />
          <TemplateHelp onClose={() => setShowHelp(false)} />
        </>
      )}
    </>
  );
}
